import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Calendar, User, Share2, Loader2, ArrowRight, CheckCircle } from 'lucide-react';
import { blogService } from '../services/blogService';
import NotFoundPage from './NotFoundPage';
import DefaultSEO from '../components/DefaultSEO';

const BlogDetail = () => {
    const { slug } = useParams();
    const navigate = useNavigate();
    const [blog, setBlog] = useState(null);
    const [relatedBlogs, setRelatedBlogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showToast, setShowToast] = useState(false);

    useEffect(() => {
        const fetchBlogData = async () => {
            setLoading(true);
            try {
                // Fetch the detail blog
                const detailData = await blogService.getBlogDetail(slug);
                if (detailData && detailData.data) {
                    setBlog(detailData.data);
                } else {
                    setError('Blog not found');
                    setLoading(false);
                    return;
                }

                // Fetch related blogs simultaneously
                const allBlogsData = await blogService.getBlogs();
                const blogsList = allBlogsData.data?.data || allBlogsData.data || [];

                // Exclude the current blog, randomize selection, and pick exactly 3
                const related = blogsList
                    .filter(b => b.slug !== slug)
                    .sort(() => 0.5 - Math.random())
                    .slice(0, 3);

                setRelatedBlogs(related);
                setLoading(false);
            } catch (err) {
                console.error('Error fetching blog data:', err);
                setError(err.response?.status === 404 ? 'NotFound' : 'Failed to load blog');
                setLoading(false);
            }
        };

        fetchBlogData();
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, [slug]);

    if (loading) {
        return (
            <div className="container py-5 d-flex justify-content-center align-items-center min-vh-100">
                <Loader2 className="animate-spin text-primary" size={48} />
            </div>
        );
    }

    if (error === 'NotFound' || !blog) {
        return <NotFoundPage />;
    }

    if (error) {
        return (
            <div className="container py-5 text-center min-vh-100">
                <h2 className="text-danger mb-3">Oops!</h2>
                <p className="text-muted">{error}</p>
                <button onClick={() => navigate('/blogs')} className="btn btn-primary mt-3">Back to Blogs</button>
            </div>
        );
    }

    return (
        <div className="container py-4 min-vh-100">
            <DefaultSEO title={`${blog.title} | ZoyoTrip`} description={blog.subcontent || blog.title} />

            <article className="max-w-4xl mx-auto" style={{ maxWidth: '800px' }}>
                <div className="text-center mb-4">
                    <span className="badge bg-primary bg-opacity-10 text-primary rounded-pill px-3 py-2 mb-3">Travel</span>
                    <h1 className="fw-bold display-5 mb-4">{blog.title}</h1>

                    <div className="d-flex align-items-center justify-content-center gap-4 text-muted mb-4">
                        <div className="d-flex align-items-center">
                            <User size={18} className="me-2" />
                            <span>ZoyoTrip Editorial</span>
                        </div>
                        <div className="d-flex align-items-center">
                            <Calendar size={18} className="me-2" />
                            <span>{new Date(blog.created_at).toLocaleDateString()}</span>
                        </div>
                    </div>
                </div>

                <img
                    src={blog.cover_img ? `${(process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000/api/v1').replace(/\/api\/v1\/?$/, '')}/storage/${blog.cover_img}` : "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='800' height='400' viewBox='0 0 800 400'><rect width='100%' height='100%' fill='%23e5e7eb'/><text x='50%' y='50%' dominant-baseline='middle' text-anchor='middle' font-family='sans-serif' font-size='36' font-weight='bold' fill='%239ca3af'>No Image</text></svg>"}
                    alt={blog.title}
                    className="img-fluid rounded-4 w-100 object-fit-cover shadow-sm mb-5"
                    style={{ maxHeight: '450px' }}
                />

                <div className="blog-content fs-5 lh-lg text-secondary">
                    <p className="lead fw-normal text-dark mb-4">{blog.subcontent}</p>
                    <div dangerouslySetInnerHTML={{ __html: blog.content }} />
                </div>

                <hr className="my-5" />

                <div className="d-flex justify-content-between align-items-center mb-5">
                    <div className="fw-bold text-dark">Share this article:</div>
                    <div className="d-flex gap-2">
                        <button
                            onClick={() => {
                                navigator.clipboard.writeText(window.location.href);
                                setShowToast(true);
                                setTimeout(() => setShowToast(false), 3000);
                            }}
                            className="btn btn-outline-secondary rounded-circle p-2 d-flex align-items-center justify-content-center"
                            style={{ width: '40px', height: '40px' }}
                        >
                            <Share2 size={18} />
                        </button>
                    </div>
                </div>

                {/* Related Blogs Section */}
                {relatedBlogs.length > 0 && (
                    <div className="mt-5 pt-5 border-top">
                        <h3 className="fw-bold text-dark mb-4">Related Articles</h3>
                        <div className="row g-4">
                            {relatedBlogs.map((relatedBlog) => (
                                <div key={relatedBlog.id || relatedBlog.slug} className="col-md-4">
                                    <div
                                        className="card border-0 shadow-sm rounded-4 h-100 overflow-hidden text-decoration-none related-blog-card"
                                        onClick={() => navigate(`/blogs/${relatedBlog.slug}`)}
                                        style={{ cursor: 'pointer' }}
                                    >
                                        <div style={{ height: '160px' }}>
                                            <img
                                                src={relatedBlog.cover_img ? `${(process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000/api/v1').replace(/\/api\/v1\/?$/, '')}/storage/${relatedBlog.cover_img}` : "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='400' height='250' viewBox='0 0 400 250'><rect width='100%' height='100%' fill='%23e5e7eb'/><text x='50%' y='50%' dominant-baseline='middle' text-anchor='middle' font-family='sans-serif' font-size='28' font-weight='bold' fill='%239ca3af'>No Image</text></svg>"}
                                                alt={relatedBlog.title}
                                                className="w-100 h-100 object-fit-cover"
                                            />
                                        </div>
                                        <div className="card-body p-3 d-flex flex-column">
                                            <h6 className="fw-bold text-dark text-truncate-2 mb-2 line-clamp-2" title={relatedBlog.title}>
                                                {relatedBlog.title}
                                            </h6>
                                            <div className="mt-auto d-flex align-items-center text-primary small fw-semibold">
                                                Read More <ArrowRight size={14} className="ms-1" />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </article>

            <style jsx>{`
                .animate-spin { animation: spin 1s linear infinite; }
                @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
                @keyframes fadeInUpToast {
                    from { opacity: 0; transform: translate(-50%, 20px); }
                    to { opacity: 1; transform: translate(-50%, 0); }
                }
                .related-blog-card { transition: transform 0.2s ease, box-shadow 0.2s ease; }
                .related-blog-card:hover { transform: translateY(-4px); box-shadow: 0 0.5rem 1rem rgba(0,0,0,0.1)!important; }
                .line-clamp-2 { display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
            `}</style>

            {/* Custom Toast Notification */}
            {showToast && (
                <div 
                    className="position-fixed bottom-0 start-50 mb-5 bg-dark text-white px-4 py-3 rounded-pill shadow flex-row d-flex align-items-center gap-2"
                    style={{ animation: 'fadeInUpToast 0.3s ease-out forwards', zIndex: 1050, transform: 'translateX(-50%)' }}
                >
                    <CheckCircle size={20} className="text-success" />
                    <span className="fw-semibold" style={{fontSize: '14px'}}>Link copied to clipboard!</span>
                </div>
            )}
        </div>
    );
};

export default BlogDetail;